// The store admin's setup checklist: what has to be decided and configured to
// stand a store up, in the order it has to happen.
//
// This sits alongside setupModules.ts rather than inside it. A MODULE is a group
// of pages with a guided tour; a SECTION is a group of checklist tasks the admin
// can hand to a leader as a unit. Most sections point at a module for their tour,
// but the two are not the same thing and must not be merged — a section carries
// settings decisions the module tour never stops at.
//
// Mirrored (structure only, no copy) at server/src/config/setupChecklist.js.

import { SetupModuleKey, SetupTourStep } from './setupModules';

// 'setup'  — configure something
// 'decide' — a yes/no or now/later call
// 'learn'  — nothing to configure; the leader just needs to know how it works
export type SetupTaskKind = 'setup' | 'decide' | 'learn';

export interface SetupTask {
  id: string;
  label: string;
  note?: string;
  kind: SetupTaskKind;
  /** Where the work happens. '—' for tasks with no page (e.g. "run one real shift"). */
  route: string;
  /** [data-setup="<id>"] on the specific control, once Settings grows anchors. */
  anchor?: string;
  adminOnly?: boolean;
  /** Starter content already exists — review it, don't build from nothing. */
  seeded?: boolean;
  /** Key into setupChecklistVerify's detector map. */
  verify?: string;
  /**
   * A walkthrough for this task specifically, used instead of the module tour
   * for the page it lands on. Module tours survey a whole page; some `learn`
   * tasks need to walk one flow through it, which is a different thing.
   */
  tourSteps?: SetupTourStep[];
}

export interface SetupSection {
  id: string;
  label: string;
  blurb: string;
  /** Which module's tour stops make up this section's scoped tour. */
  module?: SetupModuleKey;
  /** Pages the module tour doesn't cover (mostly Settings). */
  extraStops?: string[];
  /** Subscription feature gate — same keys as MODULE_FEATURE_GATES. */
  feature?: string;
  /** Can the admin hand this to a leader? */
  delegable: boolean;
  /** Cannot be skipped — must be finished before the team is brought on. */
  required?: boolean;
  tasks: SetupTask[];
}

// Order is load-bearing, not cosmetic:
//   - positions/permissions before any user is added
//   - shift times before anyone reviews an FOH or kitchen checklist
//   - assign managers AFTER the full team import, not with the leaders
//   - setup sheets last, they're generated from the real roster
//   - subscription final, billing only
export const SETUP_SECTIONS: SetupSection[] = [
  {
    id: 'positions',
    label: 'Positions and access',
    blurb: "Do this before you add a single person. Position decides what each team member can see, so adding people first means going back and fixing everyone twice.",
    delegable: false,
    extraStops: ['/settings?section=positions', '/settings?section=departments', '/settings?section=store-info', '/settings?section=kitchen'],
    tasks: [
      {
        // Levels and permissions are set on the same screen, in the same visit —
        // splitting them just made an admin tick two boxes for one job.
        id: 'positions:review', kind: 'setup', adminOnly: true, seeded: true,
        label: 'Review positions, levels, and permissions',
        note: 'You start with Team Member, Trainer, Leader, and Director — rename them to what your store actually calls them and add any levels in between. Then open each one and set what it can reach: dashboard, FOH, kitchen, documentation, training, evaluations, team, leadership, analytics, safe counting, settings. This is the most important screen in setup; everything downstream inherits from it.',
        route: '/settings?section=positions', anchor: 'position-permissions'
      },
      {
        id: 'positions:departments', kind: 'setup', adminOnly: true, seeded: true,
        label: 'Review departments',
        note: 'Front Counter, Drive Thru, and Kitchen are already there. Adjust to match how you actually schedule.',
        route: '/settings?section=departments'
      },
      {
        id: 'positions:timezone', kind: 'setup', adminOnly: true,
        label: 'Confirm your timezone',
        note: 'Checklist rollover, end-of-day, safe counts, and alert send times all run on store time. Quick to verify, painful to discover wrong in week three.',
        route: '/settings?section=store-info', anchor: 'store-timezone', verify: 'timezone-set'
      },
      {
        id: 'positions:shift-times', kind: 'setup', adminOnly: true,
        label: 'Set your shift times and end-of-day',
        note: "Opening, transition, and closing times, plus the hour the day rolls over. This drives when every FOH and kitchen checklist opens and when it counts as late — set it before anyone reviews those lists. Breaks are on the same screen.",
        route: '/settings?section=kitchen', anchor: 'shift-times', verify: 'shift-times-set'
      }
    ]
  },
  {
    id: 'leaders',
    label: 'Add your leaders',
    blurb: 'Just your leadership team for now. The rest of your team comes on once the store is actually built.',
    delegable: false,
    extraStops: ['/users'],
    tasks: [
      {
        id: 'leaders:add', kind: 'setup',
        label: 'Add your leaders and directors',
        note: 'Add them individually or bulk import with the CSV template. Set position and department as you go so their access is right on first login — that is what lets you hand them a section below.',
        route: '/users', verify: 'leaders-added'
      }
    ]
  },
  {
    id: 'foh',
    label: 'Front of house',
    blurb: 'Three shift checklists, recurring cleaning, and the equipment your team reports problems on.',
    module: 'checklists', feature: 'fohTasks', delegable: true,
    extraStops: ['/settings?section=foh'],
    tasks: [
      {
        id: 'foh:checklists', kind: 'setup', seeded: true,
        label: 'FOH checklists reviewed and updated',
        note: 'Opening, transition, and closing. Add your real tasks, drop what does not apply, and order them the way the shift actually runs.',
        route: '/foh', verify: 'foh-tasks-touched'
      },
      {
        id: 'foh:cleaning', kind: 'setup', seeded: true,
        label: 'FOH cleaning and maintenance reviewed and updated',
        note: 'Eight starter tasks are loaded. Set each to weekly, monthly, or quarterly to match your real deep-clean rotation.',
        route: '/foh/cleaning'
      },
      {
        id: 'foh:equipment', kind: 'setup', seeded: true,
        label: 'FOH equipment list updated',
        note: 'Add the units you have, remove the ones you do not, so a broken-equipment report points at a real machine.',
        route: '/foh/equipment'
      },
      {
        id: 'foh:food-safety', kind: 'decide', adminOnly: true,
        label: 'Turn on FOH food safety?',
        note: 'Off by default. Mirrors the kitchen food safety module for the front counter and dining room.',
        route: '/settings?section=foh', anchor: 'foh-food-safety'
      }
    ]
  },
  {
    id: 'kitchen',
    label: 'Kitchen',
    blurb: 'The biggest single section — shift lists, cleaning, food safety, equipment, and waste. Best handed to whoever runs the back.',
    module: 'kitchen', feature: 'kitchen', delegable: true,
    tasks: [
      {
        id: 'kitchen:checklists', kind: 'setup', seeded: true,
        label: 'Kitchen shift checklists reviewed and updated',
        note: 'Opening, transition, and closing for the back of house. Assign tasks to kitchen positions so each person only sees their own list.',
        route: '/kitchen/checklists', verify: 'kitchen-checklists-touched'
      },
      {
        id: 'kitchen:cleaning', kind: 'setup', seeded: true,
        label: 'Kitchen cleaning and maintenance reviewed and updated',
        note: 'Set the cadence on each task, and flag the ones that need a photo or count as critical.',
        route: '/kitchen/cleaning'
      },
      {
        id: 'kitchen:food-safety', kind: 'setup', seeded: true,
        label: 'Food safety list reviewed',
        note: 'Items across sanitizer, equipment, hygiene, food prep, and cleaning. Set how often each is checked and how many completions it needs per day.',
        route: '/kitchen/food-safety', verify: 'food-safety-touched'
      },
      {
        id: 'kitchen:temps', kind: 'setup', seeded: true,
        label: 'Temperature ranges verified against your equipment',
        note: 'Fourteen ranges ship with the store — walk-in, freezer, hot holding, and cook and hold targets. Confirm each against your equipment and local health code before your first audit.',
        route: '/kitchen/food-safety'
      },
      {
        id: 'kitchen:equipment', kind: 'setup', seeded: true,
        label: 'Kitchen equipment list updated',
        route: '/kitchen/equipment'
      },
      {
        id: 'kitchen:waste-items', kind: 'setup',
        label: 'Waste items updated and prices set',
        note: 'Name, unit, and cost per item, split across breakfast, lunch, and dinner. The cost accuracy is what makes the waste numbers worth looking at.',
        route: '/kitchen/waste-tracker', verify: 'waste-items'
      },
      {
        id: 'kitchen:waste-goals', kind: 'setup',
        label: 'Waste goals set',
        note: 'Daily, weekly, and monthly cost targets — otherwise the tracker records numbers with nothing to measure against.',
        route: '/kitchen/waste-tracker/analytics'
      }
    ]
  },
  {
    id: 'documentation',
    label: 'Documentation',
    blurb: 'Write the policy before you need it. The first time you have to document someone is the wrong moment to be deciding what the consequences are.',
    module: 'documentation', feature: 'documentation', delegable: true, required: true,
    extraStops: ['/settings?section=discipline-policy'],
    tasks: [
      {
        // Choosing the model and configuring it happen on the same screen, and
        // the choice is meaningless until it's filled in — one job, one tick.
        id: 'documentation:policy', kind: 'decide', adminOnly: true,
        label: 'Choose points or traditional, and set it up',
        note: 'Points assigns a value to each infraction and escalates automatically once someone crosses a threshold — you then set each infraction’s value, the consequence tiers, the attention threshold, and how long points stay on a record. Traditional uses named steps (verbal, written, final) — you set the wording of each step and the order they escalate in. Pick whichever your store already runs on paper.',
        route: '/settings?section=discipline-policy'
      },
      {
        // Lands on the actual write-up form rather than the records list, and
        // walks the fields in the order you fill them in. Nothing is saved —
        // they're looking at a blank form, so it's safe to explore.
        // Opens on Disciplinary. The document-type field only renders once a
        // category is chosen, and SpotlightTour drops steps whose anchor is
        // absent when it starts — on a blank form the whole "specific reason"
        // step vanished silently.
        id: 'documentation:write-up', kind: 'learn',
        label: 'Understand how to write someone up',
        note: 'Walk the form field by field — who it’s for, what kind of record it is, and what the team member sees when it lands.',
        route: '/documentation/new?category=Disciplinary',
        tourSteps: [
          {
            anchor: 'writeup-person',
            title: 'Start with who',
            body: 'Pick the team member this record is about. Their history and current standing come along with them, so you can see what has already been logged before you add to it.'
          },
          {
            anchor: 'writeup-category',
            title: 'What kind of record is this?',
            body: 'Disciplinary is a formal step in your policy. PIP is a performance improvement plan with goals and a review date. Administrative is a note on the record — a call-out, a doctor’s note, or something positive worth keeping.'
          },
          {
            anchor: 'writeup-type',
            title: 'The specific reason',
            body: 'On the points system this is the infraction, and it carries the point value you set in your discipline policy — the consequence tier updates on its own once they cross a threshold. On the traditional system it’s the step you’re taking.'
          },
          {
            // The separate "Action Taken" field only renders once a category is
            // picked, and SpotlightTour drops steps whose anchor is missing when
            // the tour starts — so on a blank form it would silently vanish.
            // Folded in here instead of authoring a step that half the time isn't there.
            anchor: 'writeup-description',
            title: 'Write what happened, and what you did',
            body: 'Facts, not judgement: what happened, when, and who saw it — this has to hold up months later when nobody remembers the day. Once you pick a category, an Action Taken field appears below for the conversation you had and what you agreed. A record with no action reads as a complaint; one with an action reads as coaching.'
          },
          {
            anchor: 'writeup-notify',
            title: 'How the team member sees it',
            body: 'Notify them and it appears in their account for acknowledgement — they read it and sign off, and that acknowledgement is stored with the record. Leave it off while you’re still drafting.'
          }
        ]
      },
      {
        // Deep-links with the category pre-selected (handleURLParameters already
        // maps this value to category: 'PIP'), because the PIP builder only
        // renders once PIP is chosen — and SpotlightTour drops steps whose
        // anchors aren't on screen when it starts.
        id: 'documentation:pip', kind: 'learn',
        label: 'Understand PIPs',
        note: 'What a performance improvement plan is, when it beats a write-up, and how the four steps of one fit together.',
        route: '/documentation/new?suggestedAction=Performance%20Improvement%20Plan',
        tourSteps: [
          {
            anchor: 'writeup-category',
            title: 'A PIP is not a punishment',
            body: 'A write-up records something that already happened. A PIP is forward-looking: a fixed window where you and the team member agree what has to improve, what support they get, and how you will both know it worked. Reach for it when someone is capable but off track — not as a formality on the way to letting them go.'
          },
          {
            anchor: 'pip-timeline',
            title: 'Give it a real window',
            body: 'Thirty, sixty, or ninety days. Long enough for a habit to actually change, short enough that it stays urgent. An open-ended plan is one nobody finishes.'
          },
          {
            anchor: 'pip-checkins',
            title: 'The check-ins are the plan',
            body: 'Scheduled dates you sit down together and look at progress. This is the part stores skip, and it is the part that makes a PIP work — nobody should reach the end date and be surprised by how it went.'
          },
          {
            anchor: 'pip-goals',
            title: 'Goals they can measure themselves against',
            body: 'Specific and observable, not "improve attitude". If the team member cannot tell on their own whether they hit it this week, it is not a goal yet.'
          },
          {
            anchor: 'pip-resources',
            title: 'Your half of the deal',
            body: 'Training, a shadowing shift, extra coaching time. This is what separates a plan from a warning — you are committing to something too, and the record shows you did.'
          },
          {
            anchor: 'pip-criteria',
            title: 'Define success before you start',
            body: 'Written down at the beginning so the outcome is not a judgement call at the end. Both of you should be able to read this on day one and agree what passing looks like.'
          },
          {
            anchor: 'pip-consequences',
            title: 'Be straight about what happens if it does not work',
            body: 'Say it plainly and say it kindly. Someone on a PIP already knows their job may be at risk; writing it down honestly is fairer than leaving them to guess.'
          }
        ]
      },
      {
        // Opens on the Administrative category so the tour can show the type
        // options — Call Out, Doctor Note, Other — which only render once a
        // category is picked.
        id: 'documentation:notes', kind: 'learn',
        label: 'Understand call-outs and documentation notes',
        note: 'The difference between a formal write-up and a note on the record — and when a positive note is the right move.',
        route: '/documentation/new?category=Administrative',
        tourSteps: [
          {
            anchor: 'writeup-category',
            title: 'Administrative is the neutral one',
            body: 'Disciplinary is a step in your policy and PIP is a plan to fix something. Administrative is neither — it is a note on the record. It carries no points, triggers no consequence, and moves nobody closer to a final warning. Use it when something is worth remembering but nobody is in trouble.'
          },
          {
            anchor: 'writeup-type',
            title: 'Call Out, Doctor Note, or Other',
            body: 'Call Out logs an absence so the pattern is visible later — three in a month is a conversation, and without the notes nobody spots it. Doctor Note keeps the paperwork attached to the person instead of a drawer in the office. Other is the catch-all, and it is where a positive note belongs.'
          },
          {
            anchor: 'writeup-description',
            title: 'Write it like you will need it in six months',
            body: 'Who called, when, what reason they gave, who covered. Dry and factual. The value of these notes is entirely in being able to look back across a run of them and see something you could not see one at a time.'
          },
          {
            anchor: 'writeup-notify',
            title: 'Good work belongs here too',
            body: 'An Administrative note is the only kind you can log about someone doing well — covering a shift, handling a hard guest, training a new hire without being asked. Notify them and it lands in their account as recognition. A record that only ever contains bad news is one people learn to dread.'
          }
        ]
      },
      {
        id: 'documentation:privacy', kind: 'setup', adminOnly: true,
        label: 'Set who can see documentation history',
        note: 'Whether leaders can see a team member’s full history or only what they wrote, and whether peers can submit documentation at all.',
        route: '/settings?section=discipline-policy'
      }
    ]
  },
  {
    id: 'evaluations',
    label: 'Evaluations',
    blurb: 'A real decision point. Running evaluations well takes leader time — plenty of stores turn this on in month two.',
    module: 'evaluations', feature: 'evaluations', delegable: true,
    extraStops: ['/settings?section=evaluations', '/templates'],
    tasks: [
      {
        id: 'evaluations:now-or-later', kind: 'decide',
        label: 'Set this up now, or later?',
        note: 'If later, skip the rest of this section and come back. Nothing else on the list depends on it.',
        route: '—'
      },
      {
        id: 'evaluations:grading-scale', kind: 'setup', adminOnly: true, seeded: true,
        label: 'Grading scale set up',
        note: 'A 5-point scale is ready to go. Adjust the labels, values, and colors, or rebuild it to match the scale you already use.',
        route: '/settings?section=evaluations', anchor: 'grading-scales', verify: 'grading-scale-touched'
      },
      {
        id: 'evaluations:template', kind: 'setup', seeded: true,
        label: 'Template created or picked',
        note: 'Two templates ship with the store — team member performance and leadership development. Edit the questions, or build your own.',
        route: '/templates'
      },
      {
        // The module tour surveys this page (search, filters, sorting). This
        // walks the one thing the task is actually about: getting a review from
        // "not started" to sitting down with someone.
        id: 'evaluations:send', kind: 'learn',
        label: 'Understand how to send out an evaluation',
        note: 'Where to start one from, what the team member fills in, and how it gets to the sit-down review.',
        // ?view=store: the status tabs and review list this walkthrough talks
        // about only render on All Evaluations, and the page opens on Team
        // Overview — two of five steps were being dropped silently.
        route: '/evaluations?view=store',
        tourSteps: [
          {
            anchor: 'evals-4-view-toggle',
            title: 'Two ways to look at the same thing',
            body: 'Team Overview lists only the people you are the manager of — your day-to-day view. All Evaluations shows every review in the store. Neither limits what you can do; they just change who you are looking at.'
          },
          {
            anchor: 'evals-new',
            title: 'The red plus starts one',
            body: 'Available on both views. Pick the team member and the template, and the evaluation is created for them. Choose the template that matches the role — a team member and a leader should not be graded on the same questions.'
          },
          {
            anchor: 'evals-4-status-tabs',
            title: 'It lands as Pending',
            body: 'The team member fills in their own answers first, then you fill in yours. Until both halves are in, it sits under Pending — this tab is how you see what is still waiting on someone.'
          },
          {
            anchor: 'evals-list',
            title: 'Then you sit down together',
            body: 'Open the card when both sides are done. You go through it face to face, agree the scores, and complete it — that conversation is the point of the whole thing, not the form.'
          },
          {
            anchor: 'evals-templates',
            title: 'You will not have to remember',
            body: 'Once automatic scheduling is on, reviews appear in the Scheduled tab on their own cadence. Sending one by hand like this is for off-cycle reviews and new hires.'
          }
        ]
      },
      {
        id: 'evaluations:automated', kind: 'setup', adminOnly: true,
        label: 'Automated evaluations turned on',
        note: 'Set the frequency and whether cycles start from each person’s hire date, the calendar year, your fiscal year, or a date you pick. This is what stops evaluations from quietly slipping.',
        route: '/settings?section=evaluations', anchor: 'evaluation-scheduling', verify: 'eval-auto-scheduling'
      },
      {
        id: 'evaluations:workflow', kind: 'setup', adminOnly: true,
        label: 'Set the review workflow',
        note: 'Whether leaders can manage evaluations themselves, and whether a director has to approve before one closes.',
        route: '/settings?section=evaluations', anchor: 'evaluation-access'
      }
    ]
  },
  {
    id: 'training',
    label: 'Training',
    blurb: 'The section with the most ways in — send us what you have, take one from the community, or build your own.',
    module: 'training', feature: 'training', delegable: true,
    tasks: [
      {
        // A question, not a task — there is nowhere to send them until they've
        // answered it, so no route and no "Go set it up" link.
        id: 'training:have-a-plan', kind: 'decide',
        label: 'Do you already have a training plan?',
        note: 'Three ways forward: send us what you have and we will load it in for you; pull a plan from the community library and adapt it; or build one from scratch. Answer this before the two steps below — they both make more sense once you know which way you are going.',
        route: '—'
      },
      {
        id: 'training:create', kind: 'learn',
        label: 'Understand how to build a plan from scratch',
        note: 'Starting from an empty plan: setting the department, position, and length, then filling the days.',
        route: '/training/plans',
        // NOTE: this page is the plan LIBRARY, not the builder. Steps must
        // describe what is actually on screen — an earlier draft narrated the
        // build flow while pointing at the stats row and the plan list, which
        // read as nonsense next to the thing being highlighted.
        tourSteps: [
          {
            anchor: 'training-plans-4',
            title: 'Where your plans stand',
            body: 'How many plans are active, how many people have finished one, and how long they take on average. On a new store these are all zeros — that is the gap you are about to fill.'
          },
          {
            anchor: 'training-plans',
            title: 'Your plan library',
            body: 'One card per plan. Each is tied to a department and a position — "New Hire · FOH · Team Member" — because that is what decides who it can be assigned to. Open one to see the days and modules inside it.'
          },
          {
            anchor: 'training-create',
            title: 'Build one of your own',
            body: 'This opens the builder. You name it, pick the department and position it is for, and set how many days it runs — then fill each day with modules: a document to read, a video to watch, a task to be signed off. Aim for what someone can absorb in one shift, not everything you wish they knew.'
          }
        ]
      },
      {
        // Was near-identical to 'create' and shared its module tour. The distinct
        // thing about editing is that people may already be working the plan.
        id: 'training:edit', kind: 'learn',
        label: 'Understand how to change a plan people are already on',
        note: 'Opening an existing plan, adjusting a day or a module, and what it means for a trainee who is already part-way through.',
        route: '/training/plans',
        tourSteps: [
          {
            anchor: 'training-plans-5',
            title: 'Find the plan you are changing',
            body: 'Search or filter to it. Worth being sure which plan you have open before you edit — stores usually end up with several that look similar from the outside.'
          },
          {
            anchor: 'training-plans',
            title: 'Open it and change what is wrong',
            body: 'Add a module, reword one, drop a day that never gets used. Editing is the normal case, not the exception — a plan that has never been changed is usually one nobody is really using.'
          },
          {
            anchor: 'training-plans-4',
            title: 'Check who is mid-way through first',
            body: 'Active Plans is how many people are on one right now — and they all see your edit on their next login. Adding a module is fine; deleting the one they finished this morning is confusing. If the change is big, it is kinder to let the people currently on it finish the old version.'
          }
        ]
      },
      {
        // The module tour surveys this page. This walks the actual lifecycle —
        // assign, sign off, close out — which is the whole point of the task.
        // Says "a leader", not "a team member": this section sits at 07 and the
        // team is not imported until 15, so leaders are the only real people in
        // the store when someone reaches this step.
        id: 'training:complete', kind: 'learn',
        label: 'Walk a plan through to completion with one of your leaders',
        note: 'Assign it, sign off each step, and close it out. Doing it once end to end teaches more than reading about it — and your leaders are the people who will be running it for everyone else.',
        route: '/training/progress',
        tourSteps: [
          {
            anchor: 'training-progress-7',
            title: 'Assign the plan',
            body: 'Put one of your leaders on a plan — they are the ones who will be training everybody else, so they should have been through it themselves. The moment you assign it, they can see it on their own login.'
          },
          {
            anchor: 'training-progress-1',
            title: 'They work it, you sign it off',
            body: 'Open their row to see where they are. Each module gets signed off by a trainer — that signature is the point. A plan that gets ticked through at the end of the week without anyone watching them do it proves nothing.'
          },
          {
            anchor: 'training-progress-2',
            title: 'Watch for someone stuck',
            body: 'A trainee whose bar has not moved in a week is not lazy — usually nobody has been rostered to train them. This number is how you catch that before they quit.'
          },
          {
            anchor: 'training-progress-6',
            title: 'Close it out',
            body: 'Once every module is signed off, the plan completes and they move to the completed list. Say something to them when it happens — finishing training is a real milestone and it passes unmarked in most stores.'
          },
          {
            anchor: 'training-progress-3',
            title: 'Find them again tomorrow',
            body: 'Filter by plan, or flip between Active and Completed, to pick a trainee back up mid-way. Walking one person all the way through before you roll training out to the store is worth the time — you will find the module that does not make sense far faster from the trainer’s seat than by reading the plan.'
          }
        ]
      },
      {
        id: 'training:community', kind: 'learn',
        label: 'Browse the community plans',
        note: 'Plans other operators have shared. Faster than starting from a blank page.',
        route: '/training/community-plans',
        tourSteps: [
          {
            anchor: 'community-stats',
            title: 'Other operators have solved this already',
            body: 'These are real plans shared by other stores. Nobody needs to invent new-hire training from nothing — the problem is the same in every store.'
          },
          {
            anchor: 'community-filters',
            title: 'Narrow to yours',
            body: 'Filter by department and difficulty. A back-of-house plan and a front counter plan have almost nothing in common, so start by ruling out the ones that were never for you.'
          },
          {
            anchor: 'community-grid',
            title: 'Copy it, then make it yours',
            body: 'Taking a plan gives you your own copy — editing it does not affect the store that shared it. Expect to change it: their equipment, their layout, and their standards are not quite yours.'
          }
        ]
      }
    ]
  },
  {
    id: 'assessments',
    label: 'Assessments',
    blurb: 'Checking what someone actually retained, separate from whether they finished the training.',
    feature: 'training', delegable: true,
    extraStops: ['/training/assessments', '/training/assessments/create'],
    tasks: [
      {
        // One task, not two. The assessment TYPES are picked on the create
        // page, so a separate "what types are there" task landed on the same
        // screen saying half the same thing — the duplication that made
        // training's create/edit pair feel pointless.
        id: 'assessments:create', kind: 'learn',
        label: 'Understand assessments and how to build one',
        note: 'The two types, what each is for, and how you put one together.',
        route: '/training/assessments/create',
        tourSteps: [
          {
            anchor: 'assess-new-name',
            title: 'Name it for what it proves',
            body: '"Drive-Thru Window — Week 2" tells a leader when to use it. "Assessment 3" does not.'
          },
          {
            anchor: 'assess-new-type',
            title: 'Two types, and the choice matters',
            body: 'New Hire Training Assessment is one a trainer sits and runs with someone during onboarding — it lands in that trainer’s To Conduct list. Skill Assessment measures one specific competency and adds a pass mark below. Pick based on whether you are checking a new starter is on track, or testing whether someone can actually do a particular job.'
          },
          {
            // The pass-mark field only renders for skill assessments, so it has
            // no anchor of its own — a step on it would vanish on a fresh form.
            anchor: 'assess-new-questions',
            title: 'Write questions with one right answer',
            body: 'Ask about the thing itself, not the wording of the policy — "what temperature does chicken hold at" beats "which of these is the correct policy statement". On a skill assessment you will also get a pass mark below: on food safety anything under 100% is a fail, on a broad knowledge check 80% is fair. Set it to what you would genuinely act on.'
          }
        ]
      },
      // Deliberately no "send one out" task here. This section sits at 08 and
      // the team is not imported until 15 — there is nobody to send one to yet.
      // Sending is covered by the To Conduct step in 'assessments:types'.
    ]
  },
  {
    id: 'leadership',
    label: 'Leadership areas',
    blurb: "The one section you can't fully hand off — you set up the areas, then each leader builds out their own.",
    module: 'leadership', feature: 'leadership', delegable: false,
    extraStops: ['/leadership/settings'],
    tasks: [
      {
        // Areas are picked per leader, not defined store-wide — the page asks
        // "what part of the business do you own?". /leadership/settings just
        // redirects here, so point straight at the real page.
        id: 'leadership:areas', kind: 'setup', adminOnly: true,
        label: 'Set up your own area',
        note: 'Pick the part of the business you own — Kitchen, Drive Thru, Hospitality, Talent — then add the metrics and recurring tasks you want to run it by.',
        route: '/leadership/dashboard', verify: 'leadership-areas'
      },
      {
        id: 'leadership:leader-areas', kind: 'setup',
        label: 'Have each leader set up theirs',
        note: 'Same page, their own area. Their metrics, their targets, their recurring tasks — this is theirs to own, so do not fill it in for them.',
        route: '/leadership/dashboard'
      },
      {
        id: 'leadership:own-plan', kind: 'setup',
        label: 'Pick a development plan for yourself',
        note: 'Go first. Leaders take development plans more seriously when the operator is working one too.',
        route: '/leadership/development-plans'
      },
    ]
  },
  {
    id: 'team_development',
    label: 'Team development',
    blurb: 'Promotion tracks — what a team member has to do to move up, visible to them.',
    module: 'team_development', feature: 'leadership', delegable: true,
    tasks: [
      {
        // The visibility switch moved to Settings → Access Control. Its old
        // anchor no longer exists on Manage Tracks, so this task has to point at
        // Settings or the tour step silently skips.
        id: 'team_development:on-or-off', kind: 'decide',
        label: 'Turn this on now, or leave it off for now?',
        note: 'Publishing tracks makes a promise to your team. Leave it off until you are ready to hold up your end.',
        route: '/settings?section=access-control',
        anchor: 'teamdev-tracks-visibility',
        tourSteps: [
          {
            anchor: 'teamdev-tracks-visibility',
            title: 'This is the switch',
            body: 'On means every team member can see the tracks and where they stand on one. Off means you can build them quietly first. Nothing else in this section matters until you have decided which you want.'
          }
        ]
      },
      {
        // Tracks are created from Manage Tracks, per starting role.
        id: 'team_development:create', kind: 'learn',
        label: 'Understand how to create a track',
        note: 'A track is one promotion — the requirements someone has to meet to move from one role to the next.',
        route: '/team-development/my-plans',
        tourSteps: [
          {
            // Was the second half of the on-or-off task; it belongs with the
            // tracks page, which is where the ladder actually lives.
            anchor: 'teamdev-tracks-ladder',
            title: 'What they will see',
            body: 'The career path your store runs. Publishing shows this and the requirements behind it, so be sure it matches how you actually promote before you turn it on.'
          },
          {
            anchor: 'teamdev-tracks-manage',
            title: 'Tracks hang off the role they start from',
            body: 'Each rung is a role, and the tracks under it are the ways out of it — so a store can promote into several roles from the same starting point. A hollow rung means nothing leads out of that role yet.'
          },
          {
            anchor: 'teamdev-tracks-card',
            title: 'Open the one you already have',
            body: 'A "Default Trainer Readiness Track" ships with the store — Team Member to Trainer, in five steps. Edit it to see the shape of a track before you write one, and change the steps to what your store actually asks of a new trainer.'
          },
          {
            anchor: 'teamdev-tracks-create',
            title: 'Then add the moves you actually promote into',
            body: 'Only build tracks for promotions that really happen in your store. An empty Zone Leader track is worse than no track — it advertises a path you have not thought through.'
          }
        ]
      }
    ]
  },
  {
    id: 'surveys',
    label: 'Team surveys',
    blurb: 'Anonymous feedback from your team, on a cadence you set.',
    module: 'surveys', feature: 'leadership', delegable: true,
    tasks: [
      {
        id: 'surveys:what', kind: 'learn',
        label: 'Understand what this is and what it is for',
        note: 'Anonymous feedback from your team — what it is good for, and what it costs you if you ignore what comes back.',
        route: '/team-surveys',
        tourSteps: [
          {
            anchor: 'surveys-nav',
            title: 'The list, and the results',
            body: 'All Surveys is everything you have run. Dashboard is where the answers are summarised once they start coming in. Responses are never attributed to a person — that anonymity is the whole reason this works, because people will tell you things here they will not say standing in your office.'
          },
          {
            anchor: 'surveys-list',
            title: 'Only ask if you will act',
            body: 'The fastest way to make a survey useless is to run one and change nothing. A team that answers honestly and sees no response stops answering honestly — so do not start one until you are ready to do something with it.'
          }
        ]
      },
      {
        // Templates live at /team-surveys/templates, but sending starts from the
        // list — pointing at templates dropped people a step short of the action.
        id: 'surveys:send', kind: 'learn',
        label: 'Understand how to send one and read the results',
        note: 'Pick a template, choose who it goes to, and set who is allowed to see the results.',
        route: '/team-surveys',
        tourSteps: [
          {
            anchor: 'surveys-create',
            title: 'Two ways to start one',
            body: 'Quick Survey gets a short one out in a couple of minutes. Advanced Survey lets you build the questions and choose exactly who it goes to. Keep it short either way — a handful of questions gets answered, thirty get clicked through without being read.'
          },
          {
            anchor: 'surveys-filters',
            title: 'A survey has a lifecycle',
            body: 'Drafts are not out yet. Active is collecting answers. Closed has stopped. Close one deliberately once you have enough responses, rather than leaving it open forever and never drawing a line under it.'
          },
          {
            anchor: 'surveys-list',
            title: 'Reading what comes back',
            body: 'Open a survey to see its results, and set who else is allowed to. Then tell the team what you heard and what you are doing about it — that reply is what earns you an honest answer next time.'
          }
        ]
      }
    ]
  },
  {
    id: 'tools',
    label: 'Day-to-day tools',
    blurb: 'Nothing to configure in most of these — you just need to know they are there before someone asks you.',
    delegable: true,
    extraStops: ['/chat', '/calendar', '/customer-complaints', '/vendors', '/quick-links'],
    tasks: [
      {
        id: 'tools:chat', kind: 'learn',
        label: 'Team chat — understand how to create groups',
        note: 'Everyone lands in a whole-store group automatically. Create groups for shifts, departments, or your leadership team on top of that.',
        route: '/chat',
        tourSteps: [
          {
            anchor: 'tm-chat-sidebar',
            title: 'Every conversation lives here',
            body: 'Your whole store is already in one group — nobody has to be invited to it. Everything else you create sits alongside it in this list.'
          },
          {
            anchor: 'tm-chat-new',
            title: 'Make a group for a real boundary',
            body: 'Closing team, kitchen, your leadership group. Groups work when they match how the store actually splits up; a group per topic goes quiet within a week.'
          },
          {
            anchor: 'tm-chat-thread',
            title: 'This replaces the group text',
            body: 'The value is that it stays with the store — someone who leaves loses access, someone who joins gets the history, and nobody is passing round a personal phone number.'
          }
        ]
      },
      {
        id: 'tools:complaints', kind: 'learn',
        label: 'Guest recovery — understand logging and resolving a complaint',
        note: 'Walk one through end to end: log it, act on it, close it out.',
        route: '/customer-complaints',
        tourSteps: [
          {
            anchor: 'complaints-new',
            title: 'Log it while it is fresh',
            body: 'Guest, what went wrong, what you did about it. The shift leader who took the call should log it before they go home — a complaint reconstructed three days later is worth much less.'
          },
          {
            anchor: 'complaints-stats',
            title: 'The counts are the point',
            body: 'One complaint is an incident. The same complaint four times in a month is a process problem, and this is where that becomes visible instead of living in four separate memories.'
          },
          {
            anchor: 'complaints-list',
            title: 'Resolving one',
            body: 'Open it, record what you did to make it right, and close it. An open list that never closes stops being read, so treat resolution as part of logging rather than a separate chore.'
          },
          {
            anchor: 'complaints-filters',
            title: 'Narrow it down',
            body: 'Filter by status to see just what is still open — that is your actual to-do list, as opposed to the full history.'
          },
          {
            anchor: 'complaints-analytics',
            title: 'Step back periodically',
            body: 'Trends over time. Worth a look before a leadership meeting, not after every single complaint.'
          }
        ]
      },
      {
        id: 'tools:vendors', kind: 'decide',
        label: 'Vendors — add the ones you order from',
        note: 'Contacts and invoices in one place. Skip if you are managing this elsewhere.',
        route: '/vendors'
      },
      {
        id: 'tools:quick-links', kind: 'setup',
        label: 'Quick links — add the sites your team opens daily',
        note: 'HotSchedules, CFA Home, whatever else your team keeps a bookmark for. Small, and it gets used every shift.',
        route: '/quick-links'
      }
    ]
  },
  {
    id: 'optional_modules',
    label: 'Decide what to switch on',
    blurb: 'Two modules that are genuinely optional. Both are off until you turn them on, and both are better switched on deliberately than discovered by accident.',
    delegable: false,
    extraStops: ['/settings?section=access-control'],
    tasks: [
      // Both tasks land on the toggle itself. Pointing them at /safe-counting
      // and /admin/rewards sent an admin to a module they had not enabled yet —
      // and there is nothing to explain about a feature that is switched off.
      {
        id: 'optional_modules:safe-counting', kind: 'decide', adminOnly: true,
        label: 'Safe counting — do you want to use it?',
        note: 'Counts your drawers and safe on a schedule and flags a variance. Turn it on here, then set the drawer count and amount, petty cash, who is allowed to count, and the variance that triggers an alert.',
        route: '/settings?section=access-control', anchor: 'safe-counting-enable', verify: 'safe-counting-configured'
      },
      {
        id: 'optional_modules:rewards-on', kind: 'decide', adminOnly: true,
        label: 'CFA dollars — do you want to use it?',
        note: 'Leaders get a weekly allowance to hand out for good work, and team members redeem what they earn against a catalog you set. Turn it on here, then set how much each position can give per week and build the catalog. An empty catalog is worse than the feature being off.',
        route: '/settings?section=access-control', anchor: 'rewards-enable'
      }
    ]
  },
  {
    id: 'alerts',
    label: 'Alerts and your own view',
    blurb: 'Alerts come last because they depend on knowing who is who — wrong recipients are the fastest way to get your team ignoring the app. The other two are just your own preferences.',
    delegable: false,
    extraStops: ['/settings?section=notifications', '/settings?section=mobile-navigation', '/settings?section=dashboard'],
    tasks: [
      {
        // One task, not two: both landed on the same Notifications page, and an
        // admin sets every recipient in one visit rather than two.
        id: 'alerts:review', kind: 'setup', adminOnly: true,
        label: 'Review your alerts',
        note: 'Per shift — opening, transition, closing — who is notified when a checklist is not done, the deadline, and what time the reminder goes out. Then the recipients for broken equipment and for documentation. Name real people, not a catch-all: an alert that goes to everyone gets read by no one.',
        route: '/settings?section=notifications', anchor: 'operational-alerts', verify: 'alert-recipients'
      },
      // Both of these are per-user preferences, not store settings — they change
      // your own view and nobody else's. Worded so an admin does not think they
      // are choosing on the team's behalf.
      {
        id: 'alerts:mobile-nav', kind: 'setup',
        label: "Choose what's on your mobile bottom bar",
        note: 'Yours only — each person picks their own. Put the four or five things you actually open on a shift there and nothing else.',
        route: '/settings?section=mobile-navigation'
      },
      {
        id: 'alerts:dashboard', kind: 'setup',
        label: 'Set up your own dashboard',
        note: 'Also yours only. Pick the cards and quick actions you want on your landing page.',
        route: '/settings?section=dashboard'
      }
    ]
  },
  {
    id: 'team',
    label: 'Bring your team on',
    blurb: 'The store is built and your leaders have signed off on their sections. Now open the doors.',
    module: 'team', delegable: false,
    extraStops: ['/users', '/users/assign-managers'],
    tasks: [
      {
        id: 'team:import', kind: 'setup',
        label: 'Import the rest of your team',
        note: 'Bulk import from your schedule export, or send the roster to us and we will create every account for you.',
        route: '/users', verify: 'team-imported'
      },
      {
        id: 'team:assign-managers', kind: 'setup',
        label: 'Assign managers — who reports to whom',
        note: 'Now that everyone exists, build the reporting tree. It is what turns on My Leaders, routes evaluations to the right reviewer, and populates development plans.',
        route: '/users/assign-managers', verify: 'managers-assigned'
      },
    ]
  },
  {
    id: 'setup_sheets',
    label: 'Setup sheets',
    blurb: 'Left until now on purpose — a weekly setup is built from your real roster, so it is worth nothing until your whole team is in.',
    module: 'setups', feature: 'setups', delegable: true,
    tasks: [
      {
        id: 'setup_sheets:template', kind: 'setup', seeded: true,
        label: 'Adapt the weekly template to your store',
        note: '"Standard Weekly Setup" comes with four dayparts and standard positions. Rework the time blocks and positions to match your store.',
        route: '/setup-sheet-templates', verify: 'setup-template-touched'
      },
      {
        id: 'setup_sheets:first-week', kind: 'learn',
        label: 'Build your first week from your schedule',
        note: 'Export from HotSchedules, generate the setup, adjust, save, and print. Do a real week — it is the only way to know the template is right.',
        route: '/setup-sheet-builder'
      }
    ]
  },
  {
    id: 'subscription',
    label: 'Subscription',
    blurb: "Last, once you've seen the whole thing running with your own content and your own team.",
    delegable: false,
    extraStops: ['/settings?section=subscription'],
    tasks: [
      {
        // Rings the "Switch to Card" button — that is the one an operator
        // actually clicks to put a card on file.
        id: 'subscription:billing', kind: 'setup', adminOnly: true,
        label: 'Set your billing method',
        note: 'Switch to card billing so payments run automatically, rather than an invoice someone has to remember to pay.',
        route: '/settings?section=subscription', anchor: 'switch-to-card'
      }
    ]
  }
];

export const SETUP_SECTIONS_BY_ID: Record<string, SetupSection> =
  Object.fromEntries(SETUP_SECTIONS.map((s) => [s.id, s]));

export const SETUP_TASKS_BY_ID: Record<string, SetupTask> =
  Object.fromEntries(SETUP_SECTIONS.flatMap((s) => s.tasks.map((t) => [t.id, t])));

export function sectionOfTask(taskId: string): SetupSection | undefined {
  return SETUP_SECTIONS.find((s) => s.tasks.some((t) => t.id === taskId));
}

export const TASK_KIND_LABELS: Record<SetupTaskKind, string> = {
  setup: 'Set up',
  decide: 'Decide',
  learn: 'Learn'
};
